import React from 'react';
import { toast } from 'sonner';

import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useSetFirmFeatureFlagMutation } from '@/services/api/firmsService';
import { Firm } from '@/services/types/firms-types';

interface TaskPreferencesProps {
  firm: Firm;
}

export const TaskPreferences: React.FC<TaskPreferencesProps> = ({ firm }) => {
  const [setFeatureFlag, { isLoading }] = useSetFirmFeatureFlagMutation();

  const firmFlags = firm.feature_flags;
  const bumpEnabled =
    !firmFlags?.reassignment_date_bump_enabled ||
    firmFlags.reassignment_date_bump_enabled.toLowerCase() !== 'false';

  const handleToggle = async (checked: boolean) => {
    try {
      await setFeatureFlag({
        firmId: firm.id,
        name: 'reassignment_date_bump_enabled',
        value: checked ? 'true' : 'false',
      }).unwrap();
      toast.success(
        checked
          ? 'Reassignment date bump enabled'
          : 'Reassignment date bump disabled',
      );
    } catch {
      toast.error('Failed to update preference');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Task Preferences</h2>
        <p className="mt-1 text-sm font-medium text-gray-600">
          Configure how tasks behave for this firm.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label
              htmlFor="reassignment-date-bump"
              className="text-sm font-medium text-gray-900"
            >
              Auto-bump due date on reassignment
            </Label>
            <p className="text-sm font-medium text-gray-500">
              When a task is reassigned, automatically push the due date forward
              if the task is overdue or if it is due today and the current time
              is past 3:00 PM ET. Disable this to keep the original due date
              when tasks are reassigned.
            </p>
          </div>
          <Switch
            id="reassignment-date-bump"
            checked={bumpEnabled}
            onCheckedChange={handleToggle}
            disabled={isLoading}
          />
        </div>
      </div>

      {isLoading && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white font-medium px-4 py-2 rounded-lg shadow-lg">
          Saving...
        </div>
      )}
    </div>
  );
};
